import React, { useState, useEffect, useRef, useCallback } from "react";
import useSWR from "swr";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash, FloppyDisk, Eraser } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

// Pitch multiplier: sqrt(1 + (rise/12)^2)
const pitchMult = (rise) => Math.sqrt(1 + Math.pow(Number(rise) / 12, 2));

export default function Takeoff() {
  const location = useLocation();
  const { data: projects = [] } = useSWR("/projects", fetcher);
  const [projectId, setProjectId] = useState(location.state?.project_id || "");
  const [items, setItems] = useState([newItem()]);
  const [tab, setTab] = useState("manual");

  function newItem() {
    return { label: "Main roof", length: 40, width: 30, pitch: 6, waste_pct: 10, method: "manual", area_sqft: 0 };
  }

  const calc = useCallback((it) => {
    const base = Number(it.length) * Number(it.width);
    const pitched = base * pitchMult(it.pitch);
    return Math.round(pitched * (1 + Number(it.waste_pct) / 100) * 100) / 100;
  }, []);

  const updateItem = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    next[i].area_sqft = calc(next[i]);
    setItems(next);
  };

  // Recompute all areas on mount (after default values resolve)
  useEffect(() => {
    setItems((prev) => prev.map((it) => ({ ...it, area_sqft: calc(it) })));
  }, [calc]);

  const total = items.reduce((a, b) => a + Number(b.area_sqft || 0), 0);
  const squares = Math.round(total / 100 * 100) / 100;

  const save = async () => {
    if (!projectId) return toast.error("Select a project");
    const cleaned = items
      .filter(it => Number(it.length) > 0 && Number(it.width) > 0)
      .map(it => ({ ...it, area_sqft: calc(it) }));
    if (cleaned.length === 0) return toast.error("Add at least one measurement");
    try {
      await api.post("/takeoffs", { project_id: projectId, items: cleaned });
      toast.success(`Takeoff saved · ${cleaned.reduce((a,b) => a + b.area_sqft, 0).toFixed(0)} sqft`);
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="takeoff-page">
      <PageHeader
        eyebrow="// 03 / Takeoff"
        title="Measurements"
        sub="Field measurements → pitched area + waste. Manual, freehand, or satellite."
        action={
          <Button data-testid="takeoff-save" onClick={save} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
            <FloppyDisk size={14} className="mr-2" /> Save Takeoff
          </Button>
        }
      />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-zinc-200">
            <div className="border-b border-zinc-200 p-4 flex items-center gap-3 flex-wrap">
              <Label className="label-eyebrow text-zinc-600">Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="takeoff-project-select" className="rounded-none w-72"><SelectValue placeholder="Select project…" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="rounded-none bg-zinc-50 border-b border-zinc-200 w-full justify-start h-auto p-0">
                {[
                  { v: "manual", l: "MANUAL" },
                  { v: "freehand", l: "FREEHAND" },
                  { v: "map", l: "SATELLITE" },
                ].map(t => (
                  <TabsTrigger key={t.v} value={t.v} data-testid={`takeoff-tab-${t.v}`}
                    className="rounded-none label-eyebrow data-[state=active]:bg-white data-[state=active]:text-orange-600 data-[state=active]:border-b-2 data-[state=active]:border-orange-600 px-5 py-3 border-b-2 border-transparent">
                    {t.l}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="manual" className="m-0 p-0">
                <div>
                  <div className="grid grid-cols-12 px-4 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500">
                    <div className="col-span-3">Label</div>
                    <div className="col-span-2">L (ft)</div>
                    <div className="col-span-2">W (ft)</div>
                    <div className="col-span-2">Pitch /12</div>
                    <div className="col-span-2">Waste %</div>
                    <div className="col-span-1 text-right">Area</div>
                  </div>
                  {items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-100 items-center">
                      <Input data-testid={`takeoff-label-${i}`} className="col-span-3 rounded-none" value={it.label} onChange={(e) => updateItem(i, { label: e.target.value })} />
                      <Input data-testid={`takeoff-length-${i}`} type="number" className="col-span-2 rounded-none mono" value={it.length} onChange={(e) => updateItem(i, { length: e.target.value })} />
                      <Input data-testid={`takeoff-width-${i}`} type="number" className="col-span-2 rounded-none mono" value={it.width} onChange={(e) => updateItem(i, { width: e.target.value })} />
                      <Input data-testid={`takeoff-pitch-${i}`} type="number" className="col-span-2 rounded-none mono" value={it.pitch} onChange={(e) => updateItem(i, { pitch: e.target.value })} />
                      <Input data-testid={`takeoff-waste-${i}`} type="number" className="col-span-2 rounded-none mono" value={it.waste_pct} onChange={(e) => updateItem(i, { waste_pct: e.target.value })} />
                      <div className="col-span-1 text-right mono text-sm font-semibold">{Number(it.area_sqft).toFixed(0)}</div>
                    </div>
                  ))}
                  <div className="p-4 flex gap-2">
                    <Button data-testid="takeoff-add-row" variant="outline" onClick={() => setItems([...items, { ...newItem(), label: `Section ${items.length + 1}` }])} className="rounded-none border-zinc-300 text-xs uppercase font-bold tracking-wider">
                      <Plus size={14} className="mr-2" /> Add section
                    </Button>
                    {items.length > 1 && (
                      <Button variant="outline" onClick={() => setItems(items.slice(0, -1))} className="rounded-none border-zinc-300 text-xs uppercase font-bold tracking-wider">
                        <Trash size={14} className="mr-2" /> Remove last
                      </Button>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="freehand" className="m-0 p-0">
                <FreehandCanvas onComputed={(area) => {
                  const next = [...items];
                  next.push({ label: `Freehand ${items.length + 1}`, length: 0, width: 0, pitch: 4, waste_pct: 10, method: "freehand", area_sqft: Math.round(area) });
                  setItems(next);
                  toast.success(`Freehand area added: ${Math.round(area)} sqft`);
                }} />
              </TabsContent>

              <TabsContent value="map" className="m-0 p-0">
                <div className="p-10 text-center grid-paper">
                  <p className="label-eyebrow text-zinc-500">// Satellite takeoff</p>
                  <h3 className="font-display font-bold text-2xl mt-2">Map drawing requires Google Maps API key</h3>
                  <p className="text-sm text-zinc-600 mt-3 max-w-md mx-auto">
                    Provide a <code className="mono">GOOGLE_MAPS_API_KEY</code> in settings to trace roof polygons over satellite imagery. Freehand and manual modes are fully active.
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <aside className="border border-zinc-200 h-fit">
            <div className="border-b border-zinc-200 px-5 py-3">
              <p className="label-eyebrow text-zinc-500">Summary</p>
            </div>
            <div className="p-5 space-y-4">
              <SumRow k="SECTIONS" v={items.length} />
              <SumRow k="TOTAL AREA" v={`${total.toFixed(0)} sqft`} big />
              <SumRow k="SQUARES" v={`${squares}`} big accent />
              <SumRow k="BUNDLES (~3/sq)" v={`${Math.ceil(squares * 3)}`} />
            </div>
          </aside>
        </div>
      </PageBody>
    </div>
  );
}

function SumRow({ k, v, big, accent }) {
  return (
    <div className="flex justify-between items-baseline border-b border-zinc-100 pb-3 last:border-b-0">
      <span className="label-eyebrow text-zinc-500">{k}</span>
      <span className={`mono ${big ? "text-2xl font-bold" : "text-sm"} ${accent ? "text-orange-600" : "text-zinc-900"}`}>{v}</span>
    </div>
  );
}

function FreehandCanvas({ onComputed }) {
  const canvasRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [scale, setScale] = useState(0.25); // ft per pixel

  const draw = useCallback((next) => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    // Grid
    ctx.strokeStyle = "#f4f4f5";
    ctx.lineWidth = 1;
    for (let x = 0; x < c.width; x += 20) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, c.height); ctx.stroke(); }
    for (let y = 0; y < c.height; y += 20) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(c.width, y); ctx.stroke(); }
    if (next.length < 2) return;
    ctx.strokeStyle = "#EA580C";
    ctx.lineWidth = 2;
    ctx.fillStyle = "rgba(234,88,12,0.12)";
    ctx.beginPath();
    ctx.moveTo(next[0].x, next[0].y);
    for (let i = 1; i < next.length; i++) ctx.lineTo(next[i].x, next[i].y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // crosshair points
    ctx.fillStyle = "#09090b";
    next.forEach(p => { ctx.fillRect(p.x - 3, p.y - 3, 6, 6); });
  }, []);

  useEffect(() => { draw(points); }, [points, draw]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const polygonArea = (pts) => {
    if (pts.length < 3) return 0;
    let a = 0;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      a += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
    }
    return Math.abs(a) / 2; // pixels^2
  };

  const handleAdd = (e) => {
    setPoints([...points, pos(e)]);
  };

  const finish = () => {
    if (points.length < 3) return toast.error("Need at least 3 points");
    const px = polygonArea(points);
    const sqft = px * scale * scale; // pixel scale^2
    onComputed(sqft);
    setPoints([]);
  };

  return (
    <div className="p-4">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Label className="label-eyebrow text-zinc-600">Scale (ft / px)</Label>
        <Input type="number" step="0.05" data-testid="freehand-scale" value={scale} onChange={(e) => setScale(Number(e.target.value))} className="rounded-none w-28 mono" />
        <Button data-testid="freehand-finish" onClick={finish} className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase text-xs font-bold tracking-wider">Finish polygon</Button>
        <Button variant="outline" data-testid="freehand-clear" onClick={() => setPoints([])} className="rounded-none border-zinc-300 text-xs uppercase font-bold tracking-wider">
          <Eraser size={14} className="mr-2" /> Clear
        </Button>
        <span className="label-eyebrow text-zinc-500">CLICK TO ADD CORNERS · {points.length} PTS</span>
      </div>
      <canvas
        data-testid="freehand-canvas"
        ref={canvasRef}
        width={900}
        height={420}
        onClick={handleAdd}
        className="block w-full border border-zinc-300 cursor-crosshair bg-white"
      />
    </div>
  );
}
